import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { tokenInspectorApi } from '../api/tokenInspectorApi';
import type { DecodedTokenView } from '../types/tokenInspector.types';

/**
 * Query: GET /api/eraf/dev/token-inspector/current
 * Auto-fires on page mount to decode the caller's own access token.
 *
 * R7-046 fix: window 'storage' 이벤트를 구독하여 access_token 이 변경되면
 * (login/logout/refresh 등) tokenInspector 쿼리 전체를 무효화한다.
 * 이로써 다른 탭에서 사용자가 바뀌어도 UI 가 즉시 최신 데이터를 반영한다.
 */
export function useCurrentToken() {
  const queryClient = useQueryClient();

  useEffect(() => {
    function handleStorageChange(e: StorageEvent) {
      if (e.key === 'access_token') {
        void queryClient.invalidateQueries({ queryKey: ['tokenInspector'] });
      }
    }
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [queryClient]);

  return useQuery<DecodedTokenView, { code: string; message: string }>({
    queryKey: ['tokenInspector', 'current'],
    queryFn: () => tokenInspectorApi.getCurrent(),
    retry: false,
    staleTime: 0,
  });
}
